import dynamic from 'next/dynamic';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Icons from '@aurora/shared-client/public/static/graphics/processed/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import React, { useContext, useState } from 'react';
import localStyles from '../../community/Category/CategoryActionMenu/CategoryActionMenu.module.pcss';
import useTranslation from '../../useTranslation';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import useCommunityPolicies from '@aurora/shared-client/components/community/useCommunityPolicies';
import { canRevokeBadges } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import { getLog } from '@aurora/shared-utils/log';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type {
  UserEarnedBadgesQuery,
  UserEarnedBadgesQueryVariables
} from '../../../types/graphql-types';
import userEarnedBadgesQuery from '../UserBadgesWidget/UserEarnedBadges.query.graphql';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import Loading from '@aurora/shared-client/components/common/Loading/Loading';

const ManageBadgesModal = dynamic(() => import('../ManageBadgesModal/ManageBadgesModal'), {
  ssr: false
});

const log = getLog(module);

/**
 * Action Menu for badges.
 *
 * @author Matias Aguero
 */
const BadgesActionMenu: React.FC = () => {
  const { contextUser } = useContext(AppContext);
  const cx = useClassNameMapper(localStyles);
  const [showManageBadgesModal, setShowManageBadgesModal] = useState<boolean>(false);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.BADGES_ACTION_MENU
  );

  const {
    data: communityPoliciesData,
    loading: communityPoliciesLoading,
    error: communityPoliciesError
  } = useCommunityPolicies(module, {
    useCanRevokeBadges: true
  });

  const userEarnedBadgesQueryResult = useQueryWithTracing<
    UserEarnedBadgesQuery,
    UserEarnedBadgesQueryVariables
  >(module, userEarnedBadgesQuery, {
    variables: {
      id: contextUser.id
    }
  });

  if (textLoading || userEarnedBadgesQueryResult.loading || communityPoliciesLoading) {
    return <Loading />;
  }

  const {
    data: {
      user: { earnedBadges }
    }
  } = userEarnedBadgesQueryResult;

  if (communityPoliciesError) {
    log.error(`Error fetching policy information: ${communityPoliciesError.message}`);
    return null;
  }

  const canRevokeBadgesPermission: boolean = canRevokeBadges(communityPoliciesData.coreNode);

  async function onHideModal() {
    setShowManageBadgesModal(false);
    await userEarnedBadgesQueryResult.refetch({
      id: contextUser.id
    });
  }

  const displayActionMenu: boolean = canRevokeBadgesPermission && earnedBadges.length > 0;

  return (
    displayActionMenu && (
      <>
        <Dropdown
          className={cx('lia-g-ml-10')}
          title={formatMessage('actionMenu.title')}
          aria-label={formatMessage('actionMenu.ariaLabel')}
        >
          <Dropdown.Toggle
            id="lia-badges-action-menu"
            as={Button}
            aria-label={formatMessage('toggleButtonLabel')}
            className={cx('lia-g-icon-btn')}
            variant={ButtonVariant.NO_VARIANT}
          >
            <Icon
              icon={Icons.GearIcon}
              size={IconSize.PX_20}
              color={IconColor.GRAY_900}
              testId="BadgesActionMenuItem.GearIcon"
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="right"
            aria-labelledby="lia-badges-action-menu"
            className={cx('lia-dropdown-menu lia-dropdown-menu-index')}
            popperConfig={dropdownPopperConfig}
            renderOnMount
            data-testid="BadgesActionMenuItem.dropdown"
          >
            <Dropdown.Item
              key={'ManageBadges'}
              onClick={() => setShowManageBadgesModal(true)}
              className={cx('lia-dropdown-item')}
              data-testid={'BadgesActionMenuItem.ManageBadges'}
            >
              {formatMessage('ManageBadges')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        {showManageBadgesModal && (
          <ManageBadgesModal
            earnedBadges={earnedBadges}
            show={showManageBadgesModal}
            onHide={onHideModal}
          />
        )}
      </>
    )
  );
};

export default BadgesActionMenu;
